import 'flatpickr/dist/flatpickr.min.css';
import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de';
import { english } from 'flatpickr/dist/l10n/default';

function focusActiveDay(calendarContainer: HTMLElement): void {
    const target =
        calendarContainer.querySelector<HTMLElement>('.flatpickr-day.selected') ??
        calendarContainer.querySelector<HTMLElement>('.flatpickr-day.today') ??
        calendarContainer.querySelector<HTMLElement>('.flatpickr-day:not(.flatpickr-disabled):not(.prevMonthDay):not(.nextMonthDay)');

    if (target) {
        target.setAttribute('tabindex', '0');
        target.focus();
    }
}

export function initFlatpickr(): void {
    const lang = document.documentElement.lang ?? 'de';
    document.querySelectorAll<HTMLInputElement>('input[type="date"]').forEach((input) => {
        // Date fields only allow past dates by default, `data-date-range="future"` inverts that
        const allowFutureDatesOnly = input.dataset.dateRange === 'future';
        flatpickr(input, {
            locale: lang == 'de' ? German : english,
            dateFormat: 'Y-m-d',
            altInput: true,
            altFormat: lang == 'de' ? 'd.m.Y' : 'd/m/Y',
            allowInput: true,
            // Disable Flatpickr's built-in focus→open binding so that
            // altInput.focus() in onClose doesn't immediately reopen the calendar
            clickOpens: false,
            ...(allowFutureDatesOnly ? { minDate: 'today' } : { maxDate: 'today' }),

            onReady(_dates, _str, instance) {
                const alt = instance.altInput;
                const cal = instance.calendarContainer;
                if (!alt) return;

                // Transfer id so <label for="..."> still points to the visible input
                const originalId = input.id;
                if (originalId) {
                    alt.id = originalId;
                    input.removeAttribute('id');
                }

                // Copy any explicit aria-label from the original
                const ariaLabel = input.getAttribute('aria-label');
                if (ariaLabel) alt.setAttribute('aria-label', ariaLabel);

                // Copy placeholder — Flatpickr doesn't forward it to the alt input
                const placeholder = input.getAttribute('placeholder');
                if (placeholder) alt.setAttribute('placeholder', placeholder);

                // Combobox semantics — announces the calendar popup to screen readers
                alt.setAttribute('role', 'combobox');
                alt.setAttribute('aria-haspopup', 'dialog');
                alt.setAttribute('aria-expanded', 'false');
                alt.setAttribute('autocomplete', 'off');

                // Hide the original hidden input from the accessibility tree
                input.setAttribute('aria-hidden', 'true');
                input.setAttribute('tabindex', '-1');

                // Calendar dialog semantics
                if (cal) {
                    cal.setAttribute('role', 'dialog');
                    cal.setAttribute('aria-modal', 'true');
                    cal.setAttribute('aria-label', ariaLabel ?? 'Datum auswählen');

                    // Close button in the top-right corner
                    const closeBtn = document.createElement('button');
                    closeBtn.type = 'button';
                    closeBtn.className = 'flatpickr-close-btn';
                    closeBtn.setAttribute('aria-label', 'Kalender schließen');
                    // Mouse click: intercept before Flatpickr's own mousedown handler
                    closeBtn.addEventListener('mousedown', (e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        instance.close();
                    });
                    // Keyboard Enter/Space: browsers fire click (not mousedown) for button activation
                    closeBtn.addEventListener('click', (e: MouseEvent) => {
                        if (e.detail === 0) {
                            // detail === 0 means keyboard-triggered; mouse clicks are handled by mousedown
                            instance.close();
                            instance.altInput?.focus();
                        }
                    });
                    cal.appendChild(closeBtn);

                    // Close when focus leaves the calendar entirely
                    cal.addEventListener('focusout', (e: FocusEvent) => {
                        const newTarget = e.relatedTarget as Element | null;
                        if (!newTarget || (!cal.contains(newTarget) && newTarget !== alt)) {
                            instance.close();
                        }
                    });
                }

                // Click opens the calendar (replaces Flatpickr's disabled auto-open)
                alt.addEventListener('click', () => {
                    if (!instance.isOpen) instance.open();
                });

                // Enter / Space / Arrow open the calendar from keyboard
                alt.addEventListener('keydown', (e: KeyboardEvent) => {
                    if (instance.isOpen) return;
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        instance.open();
                    }
                });
            },

            onOpen(_dates, _str, instance) {
                instance.altInput?.setAttribute('aria-expanded', 'true');

                // Move focus into the calendar so arrow-key navigation works immediately
                const cal = instance.calendarContainer;
                if (cal) {
                    requestAnimationFrame(() => focusActiveDay(cal));
                }
            },

            onClose(_dates, _str, instance) {
                instance.altInput?.setAttribute('aria-expanded', 'false');
                // Safe to focus now — clickOpens: false means this won't reopen the calendar
                instance.altInput?.focus();
            },
        });
    });
}
